import { useCargaRutas, type CargaRuta } from '../../hooks/useCargaRutas'
import { colorDeDia } from './coloresDia'

const DIA_POR_R: Record<string, string> = {
  r1: 'lunes', r2: 'martes', r3: 'miércoles', r4: 'jueves', r5: 'viernes',
}

/**
 * Panel de carga por ruta (R7 · B.7).
 *
 * Muestra cuántos clientes van cada día de la semana (r1..r5), con el color de
 * cada ruta y una barra proporcional. Qué cuenta depende del contexto:
 *  - Por defecto (sin props): los clientes ya AGENDADOS por ruta (hook propio).
 *  - En "Esta semana": se le pasa `distribucion` = el reparto de los AVISOS por
 *    día (cargaPorDia de gestionSemana), que es lo que se repartirá esta semana.
 */
export function CargaRutas({
  distribucion,
  titulo = 'Carga por ruta',
  etiquetaTotal,
}: {
  distribucion?: { r1: number; r2: number; r3: number; r4: number; r5: number }
  titulo?: string
  etiquetaTotal?: (total: number) => string
} = {}) {
  const hook = useCargaRutas()
  const usandoDistribucion = distribucion != null
  const rutas: CargaRuta[] = usandoDistribucion
    ? (['r1', 'r2', 'r3', 'r4', 'r5'] as const).map((r) => ({ ruta: r, dia: DIA_POR_R[r], carga: distribucion[r] }))
    : hook.data ?? []
  const isPending = usandoDistribucion ? false : hook.isPending
  const isError = usandoDistribucion ? false : hook.isError
  const maximo = Math.max(1, ...rutas.map((r) => r.carga))
  const total = rutas.reduce((suma, r) => suma + r.carga, 0)

  if (isError) return null // el panel principal ya avisa; esto es complementario

  const textoTotal = etiquetaTotal
    ? etiquetaTotal(total)
    : `${total} repartos esta semana`

  return (
    <section
      aria-label="Carga por ruta de la semana"
      className="flex flex-col gap-3 rounded-xl border border-borde bg-superficie p-4"
    >
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-tinta">{titulo}</h2>
        <span className="text-xs text-tinta-apagada">
          {isPending ? 'Cargando…' : textoTotal}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        {(isPending ? PLACEHOLDER : rutas).map((r) => (
          <BarraRuta key={r.ruta} ruta={r} maximo={maximo} cargando={isPending} />
        ))}
      </div>
    </section>
  )
}

const PLACEHOLDER: CargaRuta[] = [
  { ruta: 'r1', dia: 'lunes', carga: 0 },
  { ruta: 'r2', dia: 'martes', carga: 0 },
  { ruta: 'r3', dia: 'miércoles', carga: 0 },
  { ruta: 'r4', dia: 'jueves', carga: 0 },
  { ruta: 'r5', dia: 'viernes', carga: 0 },
]

function BarraRuta({ ruta, maximo, cargando }: { ruta: CargaRuta; maximo: number; cargando: boolean }) {
  const pct = Math.round((ruta.carga / maximo) * 100)
  const color = colorDeDia(ruta.ruta)
  return (
    <div className="flex items-center gap-3">
      <span className="w-20 shrink-0 text-xs font-medium capitalize text-tinta-2">{ruta.dia}</span>
      <div className="relative h-5 flex-1 overflow-hidden rounded-md bg-elevado">
        {!cargando ? (
          <div
            className={`h-full rounded-md ${color.fondo} transition-all`}
            style={{ width: `${Math.max(pct, ruta.carga > 0 ? 6 : 0)}%` }}
          />
        ) : null}
      </div>
      <span className="w-8 shrink-0 text-right text-sm font-semibold tabular-nums text-tinta">
        {cargando ? '·' : ruta.carga}
      </span>
    </div>
  )
}
